/* eslint-disable react-native/no-inline-styles */
import React, { useState, useRef } from 'react';
import { View, Text, KeyboardAvoidingView } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';
import moment from 'moment';
import { parse } from 'search-params';

import { GridBox, scale, notchTop } from '@components/frame';
import { Alert, getSecureValue, isWeb } from '@helpers';

import AnimatedHeader from '@components/common/AnimatedHeader';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';

import Button from '@components/button';
import SuccessScreen from '@components/connectorSetup/components/SuccessScreen';
import RunningConnector from '@components/connectorSetup/components/RunningConnector';
import { SmallLoading } from '@components/loading';
import CodeInput from '@components/codeInput';
import Scanner from '@components/connectorSetup/components/Scanner';

import { validateOnboarding, onboardConnector, saveData, logOnboarding } from '@reducers/onBoarding';
import { SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';

const LinkConnector = ({ C, onBoarding, validateOnboarding, onboardConnector, saveData, logOnboarding }) => {
  const globalCode = onBoarding?.unregisteredConnector?.sessionKey;

  const min15 = 15 * 60;
  const pass = moment.utc().diff(moment.utc(onBoarding?.unregisteredConnector?.startedOn), 's');
  const left = min15 - pass;

  const isRunningConnector = onBoarding?.registeredConnector?.isRunning;

  const [loading, setLoading] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [code, setCode] = useState('');
  const scanned = useRef(false);
  const [showScanner, setShowScanner] = useState(false);

  const invalidQr = () => {
    logOnboarding(`Component: Link Connector. Action: User scanned wrong QR code`, {
      component: 'Link Connector',
      action: 'User scanned wrong QR code',
    });
    setLoading(false);
    scanned.current = false;
    Alert({ title: i18n.t('Error'), text: i18n.t('Wrong QR code') });
  };

  const handleBarCodeScanned = async (code) => {
    try {
      const data = code.value;

      scanned.current = true;
      setLoading(true);

      if (!data) {
        return invalidQr();
      }

      const isQRLink = data.indexOf('/qr?') > 0;
      if (isQRLink) {
        const { sessionKey } = parse(data);

        if (sessionKey) {
          return await submit(sessionKey);
        }

        return invalidQr();
      }
    } catch (e) {
      console.log(e, 'e');
      invalidQr();
    }
  };

  const onInput = async (text) => {
    setCode(text);
    if (text.length === 8) {
      logOnboarding(`Component: Link Connector. Action: User entered code. Code: ${text}`, {
        component: 'Link Connector',
        action: 'User entered code',
      });
      await submit(text);
    }
  };

  const submit = async (c) => {
    setLoading(true);
    const code = c || globalCode;
    const isAuthorized = getSecureValue('token');
    if (isAuthorized) {
      logOnboarding(`Component: Link Connector. Action: Start onboarding`, { component: 'Link Connector', action: 'Start onboarding' });
      saveData({ code });
      const result = await onboardConnector(null, false);
      if (result?.connectorId) {
        setSuccess(true);
      } else {
        Alert({ title: result?.message || 'Wrong code' });
      }
    } else {
      logOnboarding(`Component: Link Connector. Action: Validate onboarding`, { component: 'Link Connector', action: 'Validate onboarding' });
      const result = await validateOnboarding(code, true);
      if (result.onboardingStatus === 'UnregisteredConnectorConnectionEstablished') {
        setSuccess(true);
      } else {
        Alert({ title: 'Wrong code' });
      }
    }
    setLoading(false);
  };

  if (isRunningConnector) {
    return <RunningConnector {...{ C, onBoarding, backgroundColor: C('Background/Light/Detail'), logOnboarding }} />;
  }

  if (isSuccess) {
    return <SuccessScreen {...{ C, backgroundColor: C('Background/Light/Detail'), logOnboarding }} />;
  }

  if (loading) {
    return (
      <View style={{ width: '100%', flex: 1, paddingTop: notchTop, backgroundColor: C('Background/Light/Detail') }}>
        <View style={{ width: '100%', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <SmallLoading />
        </View>
      </View>
    );
  }

  if (showScanner) {
    return <Scanner {...{ C, scanned, handleBarCodeScanned, setShowScanner }} />;
  }

  const displaySteps = {
    auto: Boolean(globalCode) && left > 0,
    scan: !isWeb,
    manual: true,
  };

  let amount = 0;
  Object.values(displaySteps).forEach((i) => {
    if (i) {
      amount += 1;
    }
  });

  const renderStep = (name) => {
    switch (name) {
      case 'auto': {
        return (
          <View style={{ width: '100%' }}>
            <Text style={[font['Body'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>
              {i18n.t('Automatically link with the Connector that is running on your local network.')}
            </Text>
            <Text style={[font['Body'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>
              {onBoarding?.unregisteredConnector?.localHost || ''}
            </Text>
            <Text style={[font['Body'], { color: C('Label/Light/Primary'), textAlign: 'center', marginBottom: scale(12) }]}>
              {onBoarding?.unregisteredConnector?.localDnsSuffix || ''}
            </Text>
            <Button
              {...{
                name: i18n.t(`Automatically Link`),
                onPress: async () => {
                  logOnboarding(`Component: Link Connector. Action: Automatically Link`, {
                    component: 'Link Connector',
                    action: 'Automatically Link',
                  });
                  submit();
                },
                height: scale(53),
                width: '100%',
                bg: C('Button/Light/Primary'),
              }}
            />
          </View>
        );
      }
      case 'scan': {
        return (
          <View style={{ width: '100%' }}>
            <Button
              {...{
                name: i18n.t(`Scan QR code of Connector`),
                onPress: async () => {
                  logOnboarding(`Component: Link Connector. Action: Scan QR code of Connector`, {
                    component: 'Link Connector',
                    action: 'Scan QR code of Connector',
                  });
                  setShowScanner(true);
                },
                height: scale(53),
                width: '100%',
                bg: C('Button/Light/Primary'),
              }}
            />
          </View>
        );
      }
      case 'manual': {
        return (
          <View>
            <View style={{ width: '100%' }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                <CodeInput {...{ C, value: code, setValue: onInput }} />
              </View>
            </View>
            <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginTop: scale(12), textAlign: 'center' }]}>
              {i18n.t('Enter the link code shown on your Connector')}
            </Text>
          </View>
        );
      }
    }
  };
  return (
    <View style={{ height: '100%' }} key="device-details">
      <AnimatedHeader
        {...{
          backText: i18n.t('Settings'),
          text: '',
          C,
          animated: false,
          back: true,
          backgroundColor: 'Background/Light/Detail',
          borderBottomWidth: 0,
        }}
      />
      <View style={{ flex: 1, justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
        <View style={{ marginTop: scale(-60), minHeight: scale(160), paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL), maxWidth: scale(320) }}>
          <Text style={[font['Title 2'], { color: C('Label/Light/Primary'), textAlign: 'center', marginBottom: scale(52) }]}>
            {amount == 1 ? i18n.t('Link Connector') : i18n.t('{{amount}} ways to Link Connector', { amount })}
          </Text>
          <KeyboardAvoidingView behavior="position" enabled>
            <View>
              {Object.keys(displaySteps).map((key) => {
                if (!displaySteps[key]) {
                  return;
                }
                let number = 0;
                Object.keys(displaySteps).find((i) => {
                  if (displaySteps[i]) {
                    number += 1;
                  }
                  if (i === key) {
                    return true;
                  }
                });
                return (
                  <View key={key} style={{ marginBottom: scale(52) }}>
                    {amount == 1 ? null : (
                      <Text style={[font['Title 2'], { color: C('Label/Light/Primary'), textAlign: 'center', marginBottom: scale(12) }]}>
                        {number}
                      </Text>
                    )}
                    {renderStep(key)}
                  </View>
                );
              })}
            </View>
          </KeyboardAvoidingView>
        </View>
      </View>
    </View>
  );
};

const WrapperComponent = (props) => {
  const { C } = props;
  return (
    <GridBox
      {...{
        backgroundColor: C('Background/Light/Detail'),
        headerOffset: true,
        notchOffset: false,
        sliderOffset: false,
      }}
    >
      <LinkConnector {...props} />
    </GridBox>
  );
};

export default connect(({ onBoarding }) => ({ onBoarding }), { validateOnboarding, onboardConnector, saveData, logOnboarding })(
  ColorWrapper(WrapperComponent)
);
